import { create } from 'zustand';
import { persist, subscribeWithSelector } from 'zustand/middleware';
import { addMissingEntriesFromCachedAutoL2imageToL2imageBulk, cleanDict2IfNotInDict1, countNodes, customStorage, load, mergeNodes, store } from '../foo';
import { _cachedLocalAutoL2image, _defaultParams, _freeCards, _l2audio, _l2color, _l2image, _l2visibility, _nodes, _pathSeparator, _storageVersion } from '../constants';
import { mergeImportedNodes, removeTrueEntries, mergeL2Image, simpleDictUpdate, mapNodesValuesChars, mapKeysInL2Dict, cutTree, replaceKeyPrefixes, getFirstFreeCards, filterDictByKeys, mergeCachedDicts, cleanCachedDicts, getLabelRootName } from '../../foo/tree';
import { useURLStore } from './URLZustand';
import { useUserStore } from './userZustand';
 

interface TreeState {
    version: number;
    migrate: () => void;
    isTreeStoreInitialized: boolean;
    setTreeStoreInitialized: (isTreeStoreInitialized: boolean) => void;

    freeNodes: string[];
    updateFreeNodes: () => void;
    nodes: { [key: string]: string[] };
    nodesCount: number;
    l2Visibility: { [key: string]: boolean };
    l2Audio: { [key: string]: any };
    l2Image: { [key: string]: string };  
    l2Color: { [key: string]: string };
    cachedLocalAutoL2image: { [key: string]: string[] };
    homeworks: string[];
    addToHomeworks: (newHomeworks: string[]) => void;
    removeFromHomeworks: (homeworksToRemove: string[]) => void;
    //REF-D
    pathFlags: { [key: string]: string[] };
    pathMultipleChoices: { [key: string]: string[] };
    path2Params: { [key: string]: { [key:string] : any }};
    setPath2Params: (path2Params: TreeState["path2Params"]) => void;
    defaultParams: { [key: string]: any };
    setDefaultParams: (defaultParams: TreeState["defaultParams"]) => void;
    
    // key = path, value = { templateId : templateId, id: identifier}
    activityDict : { [key: string]: { [key:string] : any } };
    setActivityDict: (activityDict: { [key: string]: { [key:string] : any } }) => void;

    setNodes: (nodes: TreeState["nodes"]) => void;
    setL2Visibility: (l2Visibility: TreeState["l2Visibility"]) => void;
    setL2Audio: (l2Audio: TreeState["l2Audio"]) => void;
    setL2Image: (l2Image: TreeState["l2Image"]) => void;
    setL2Color: (l2Color: TreeState["l2Color"]) => void;
    setCachedLocalAutoL2image: (cachedLocalAutoL2image: any) => void;
    updateTree: (nodes: TreeState["nodes"], l2Image: TreeState["l2Image"], cachedLocalAutoL2image: TreeState["cachedLocalAutoL2image"]) => void;
    updateCardToggle: boolean;

    //REF-D
    setPathFlags: (pathFlags: TreeState["pathFlags"]) => void;
    setPathMultipleChoices: (pathMultipleChoices: TreeState["pathMultipleChoices"]) => void;

    snapshot: { nodes: { [key: string]: string[] }, l2Visibility: { [key: string]: boolean }, l2Audio: { [key: string]: any }, l2Image: { [key: string]: string }, l2Color: { [key: string]: string }, cachedLocalAutoL2image: { [key: string]: string[] }} | null;
    takeSnapshot: (labels:string[]) => void;
    restoreFromSnapshot: () => void;
    resetSnapshot: () => void;

    importPecs: (obj:any, startPath?:string, selectedLabelsKeys?:string[], avoidDuplicates?:boolean) => void;
    pasteNodes: (labels:string[], fromPath:string, toPath:string, selectedLabelsKeys?:string[], avoidDuplicates?:boolean) => void;

    updateVisibleLabels: (nodes: TreeState["nodes"]) => void;

    getImage: (label: string) => string;
};

const defaultTreeState: TreeState = {
    version: 0,
    migrate: () => {},
    isTreeStoreInitialized: false,
    setTreeStoreInitialized: () => {},

    freeNodes: [],
    updateFreeNodes: () => {},

    nodes: _nodes,
    nodesCount: 0,
    l2Visibility: _l2visibility,
    l2Audio: _l2audio,
    l2Image: _l2image,
    l2Color: _l2color,
    cachedLocalAutoL2image: _cachedLocalAutoL2image,
    updateCardToggle: false,
    homeworks: [],
    addToHomeworks: () => {},
    removeFromHomeworks: () => {},
    //REF-D
    pathFlags: {},
    pathMultipleChoices: {},
    path2Params: {},
    setPath2Params: () => {},
    defaultParams: _defaultParams,
    setDefaultParams: () => {},

    snapshot: null,
    takeSnapshot: () => {},
    restoreFromSnapshot: () => {},
    resetSnapshot: () => {},

    activityDict: {},
    setActivityDict: () => {},
    
    setNodes: () => {},
    setL2Visibility: () => {},
    setL2Audio: () => {},
    setL2Image: () => {},
    setL2Color: () => {},
    setCachedLocalAutoL2image: () => {},
    updateTree: () => {},
    //REF-D
    setPathFlags: () => {},
    setPathMultipleChoices: () => {},

    importPecs: () => {},
    pasteNodes: () => {},

    updateVisibleLabels: () => {},

    getImage: () => "",
    
};

export const useTreeStore = create<TreeState>()(
    subscribeWithSelector(
        persist(
        (set, get) => ({
            ...defaultTreeState,

            setTreeStoreInitialized: (isTreeStoreInitialized: boolean) => {
                set({ isTreeStoreInitialized: isTreeStoreInitialized });
            },
            migrate: async () => {
                if(get().version === 0){                
                    const nodes = await load("nodes", _nodes);
                    const l2visibility = await load("visibility", _l2visibility);
                    const l2audio = await load("audio", _l2audio);
                    const l2image = await load("image", _l2image);
                    const l2color = await load("color", _l2color);
                    const cachedLocalAutoL2image = await load("cachedLocalAutoL2image", _l2color);

                    if(Object.keys(nodes).length > 0 
                    || Object.keys(l2visibility).length > 0
                    || Object.keys(l2audio).length > 0
                    || Object.keys(l2image).length > 0
                    || Object.keys(l2color).length > 0
                    || Object.keys(cachedLocalAutoL2image).length > 0){
                        set({
                            ...defaultTreeState,
                            version: _storageVersion,
                            nodes: mapNodesValuesChars(nodes),
                            nodesCount: countNodes(nodes),
                            l2Visibility: cleanDict2IfNotInDict1(nodes, mapKeysInL2Dict(l2visibility)),
                            l2Audio: cleanDict2IfNotInDict1(nodes, mapKeysInL2Dict(l2audio)),
                            l2Image: cleanDict2IfNotInDict1(nodes, mapKeysInL2Dict(l2image)),
                            l2Color: cleanDict2IfNotInDict1(nodes, mapKeysInL2Dict(l2color)),
                            cachedLocalAutoL2image: cachedLocalAutoL2image,
                        });

                        // Delete old storage keys
                        customStorage.removeItem("nodes");
                        customStorage.removeItem("visibility");
                        customStorage.removeItem("audio");
                        customStorage.removeItem("image");
                        customStorage.removeItem("color");
                        customStorage.removeItem("cachedLocalAutoL2image");
                    }
                }
            },            

            setNodes: (nodes: TreeState["nodes"]) => {
                nodes = mapNodesValuesChars(nodes);
                get().updateVisibleLabels(nodes);
                const { isSubscribed } = useUserStore.getState();
                set({
                    nodes: nodes,
                    nodesCount: countNodes(nodes),
                    freeNodes: getFirstFreeCards(nodes, isSubscribed || false)
                });
            },
            setDefaultParams: (defaultParams: TreeState["defaultParams"]) => {
                set({ defaultParams: defaultParams });
            },
            setPath2Params: (path2Params: TreeState["path2Params"]) => {
                set({ path2Params: path2Params });
            },
            
            addToHomeworks(newHomeworks) {
                const homeworks = get().homeworks;
                const uniqueHomeworks = [...new Set([...homeworks, ...newHomeworks])];
                set({ homeworks: uniqueHomeworks });
            },
            removeFromHomeworks(homeworksToRemove) {
                const homeworks = get().homeworks;
                const newHomeworks = homeworks.filter(item => !homeworksToRemove.includes(item));
                set({ homeworks: newHomeworks });
            },
            takeSnapshot: (labels:string[]) => {
                const {URLVars} = useURLStore.getState();
                // copy only current notepath to snapshot nodes
                let snapshotNodes: {[key:string]:string[]} = {};
                snapshotNodes[URLVars.nodePath!] = [...get().nodes[URLVars.nodePath!]];

                let snapshotL2Visibility = filterDictByKeys(get().l2Visibility, labels);
                let snapshotL2Audio = filterDictByKeys(get().l2Audio, labels);
                let snapshotL2Image = filterDictByKeys(get().l2Image, labels);
                let snapshotL2Color = filterDictByKeys(get().l2Color, labels);
                let snapshotCachedLocalAutoL2image = filterDictByKeys(get().cachedLocalAutoL2image, labels);
                
                set({
                    snapshot: { 
                        nodes: snapshotNodes,
                        l2Visibility: snapshotL2Visibility,
                        l2Audio: snapshotL2Audio,
                        l2Image: snapshotL2Image,
                        l2Color: snapshotL2Color, 
                        cachedLocalAutoL2image: snapshotCachedLocalAutoL2image
                    }                    
                });
            },

            restoreFromSnapshot: () => {
                const snapshot = get().snapshot;
                if (snapshot !== null) {
                    const { nodes, l2Visibility, l2Audio, l2Image, l2Color, cachedLocalAutoL2image } = snapshot;
                    const { isSubscribed } = useUserStore.getState();
                    set({
                        nodes: { ...get().nodes, ...nodes},
                        nodesCount: countNodes(nodes),
                        freeNodes: getFirstFreeCards(nodes, isSubscribed || false),
                        l2Visibility: { ...get().l2Visibility, ...l2Visibility },
                        l2Audio: { ...get().l2Audio, ...l2Audio },
                        l2Image: { ...get().l2Image, ...l2Image },
                        l2Color: { ...get().l2Color, ...l2Color },
                        cachedLocalAutoL2image: { ...get().cachedLocalAutoL2image, ...cachedLocalAutoL2image },
                        snapshot: null
                        
                    });
                }
                
            },

            updateFreeNodes: () => {
                const { isSubscribed } = useUserStore.getState();
                const nodes = get().nodes;
                set({ freeNodes: getFirstFreeCards(nodes, isSubscribed || false) });
            },
 
            setL2Visibility: (l2visibility: TreeState["l2Visibility"]) => {
                
                set((state:TreeState) => ({ l2Visibility: cleanDict2IfNotInDict1(state.nodes, mapKeysInL2Dict(removeTrueEntries(l2visibility)))}))
            },
  
            setL2Audio: (l2Audio: TreeState["l2Audio"]) => {
                set((state:TreeState) => ({ l2Audio: cleanDict2IfNotInDict1(state.nodes, mapKeysInL2Dict(l2Audio))}))
            },

            setL2Image: (l2Image: TreeState["l2Image"]) => {
                set((state:TreeState) => ({ l2Image: cleanDict2IfNotInDict1(state.nodes,mapKeysInL2Dict(l2Image))}))
            },

            setL2Color: (l2Color: TreeState["l2Color"]) => {
                set((state:TreeState) => ({ l2Color: cleanDict2IfNotInDict1(state.nodes,mapKeysInL2Dict(l2Color))}))
            },

            setCachedLocalAutoL2image: async (cachedLocalAutoL2image: TreeState["cachedLocalAutoL2image"]) => {
                cachedLocalAutoL2image = mapKeysInL2Dict(cachedLocalAutoL2image);
                const currentState = get();
                const l2image = await addMissingEntriesFromCachedAutoL2imageToL2imageBulk(currentState.l2Image, cachedLocalAutoL2image, currentState.nodes);

                set((state: TreeState) => ({
                    cachedLocalAutoL2image: cachedLocalAutoL2image,
                    l2Image: l2image
                }));
            },
            //REF-D
            setPathFlags: (pathFlags: TreeState["pathFlags"]) => {
                set({ pathFlags: pathFlags });
            },
            setPathMultipleChoices: (pathMultipleChoices: TreeState["pathMultipleChoices"]) => {
                set({ pathMultipleChoices: pathMultipleChoices });
            },   
            
            updateTree: async (
                nodes: TreeState["nodes"],
                l2Image: TreeState["l2Image"],
                cachedLocalAutoL2image: TreeState["cachedLocalAutoL2image"]) => {
                nodes = mapNodesValuesChars(nodes);
                l2Image = mapKeysInL2Dict(l2Image);
                cachedLocalAutoL2image = mapKeysInL2Dict(cachedLocalAutoL2image);
                
                get().updateVisibleLabels(nodes);
                const { isSubscribed } = useUserStore.getState();
                set({
                    nodes: nodes,
                    nodesCount: countNodes(nodes),
                    freeNodes: getFirstFreeCards(nodes, isSubscribed || false),
                    l2Image: cleanDict2IfNotInDict1(nodes,l2Image),
                    cachedLocalAutoL2image: cachedLocalAutoL2image,
                });
            },

            importPecs: (obj:any, startPath:string="", selectedLabelsKeys:string[] = [], avoidDuplicates = false) => {
                
                // if selectedLabelsKeys is provided, the import will be pasted to each of the selected labels
                obj.nodes = mapNodesValuesChars(obj.nodes);
                obj.l2Image = mapKeysInL2Dict(obj.l2Image);
                obj.l2Audio = mapKeysInL2Dict(obj.l2Audio || {});
                if(obj.cachedLocalAutoL2image !== undefined){
                    obj.cachedLocalAutoL2image = (obj.cachedLocalAutoL2image)
                }                
                
                const currentState = get();
                const { URLVars } = useURLStore.getState();
                let newNodes = { ...currentState.nodes }; // Create a copy of nodes

                // Define the internal function to update the state
                const updateState = (destinationPath:string, avoidDuplicates:boolean) => {
                    newNodes = mergeImportedNodes(newNodes, obj.nodes, destinationPath, avoidDuplicates);
                    let newL2Image;
                    let newCachedLocalAutoL2image;
                    if(obj.cachedLocalAutoL2image === undefined){
                        const { mergedDict, cache } = mergeL2Image(currentState.l2Image, obj.l2Image, currentState.cachedLocalAutoL2image);
                        newL2Image = mergedDict;
                        newCachedLocalAutoL2image = cache;
                    }else{
                        newL2Image = simpleDictUpdate(currentState.l2Image, obj.l2Image);
                        newCachedLocalAutoL2image = mergeCachedDicts(currentState.cachedLocalAutoL2image, obj.cachedLocalAutoL2image)
                    }
                    // clean newCachedLocalAutoL2image
                    newCachedLocalAutoL2image = cleanCachedDicts(newCachedLocalAutoL2image);
                    const newL2Audio = simpleDictUpdate(currentState.l2Audio, obj.l2Audio);
            
                    get().updateVisibleLabels(newNodes);
                    const { isSubscribed } = useUserStore.getState();
                    set({
                        nodes: newNodes,
                        nodesCount: countNodes(newNodes),
                        freeNodes: getFirstFreeCards(newNodes, isSubscribed || false),
                        l2Image: newL2Image,
                        l2Audio: newL2Audio,
                        cachedLocalAutoL2image: newCachedLocalAutoL2image,
                    });
                };
            
                // Use the internal function to update the state for each individual label in selectedLabels
                // or for the single path specified by startPath if selectedLabelsKeys is empty
                if (selectedLabelsKeys && selectedLabelsKeys.length > 0) {
                    const { URLVars } = useURLStore.getState();
                    selectedLabelsKeys.forEach(label => {
                        updateState(URLVars.nodePath+_pathSeparator+label, avoidDuplicates);
                    });
                } else {
                    updateState(startPath === "" ? URLVars.nodePath! : startPath, avoidDuplicates);
                }
            },
            pasteNodes: (labels, fromPath, toPath, selectedLabelsKeys, avoidDuplicates=true) => {
                const nodes = get().nodes;
                const prefixes = labels.map(item => fromPath+_pathSeparator+item);
                let subBranchesNode = cutTree(nodes, prefixes);
                subBranchesNode = replaceKeyPrefixes(subBranchesNode, fromPath, "");
             
                let newNodes = { ...nodes }; // Create a copy of nodes
            
                // Define the internal function to paste labels to a given destination path
                const pasteLabelsToDestination = (destinationPath:string, avoidDuplicates:boolean=true) => {
                    newNodes = mergeImportedNodes(newNodes, subBranchesNode, destinationPath);
                    let existingLabels = newNodes[destinationPath] || [];
                    if(avoidDuplicates === true){
                        newNodes[destinationPath] = Array.from(new Set([...existingLabels, ...labels]));
                    }else if(avoidDuplicates === false){
                        newNodes[destinationPath] = [...existingLabels, ...labels];
                    }
                };
            
                // Use the internal function to paste labels to each individual path in selectedLabels
                // or to the single path specified by toPath if selectedLabelsKeys is empty
                if (selectedLabelsKeys && selectedLabelsKeys.length > 0) {
                    const { URLVars } = useURLStore.getState();
                    selectedLabelsKeys.forEach(label => {
                        pasteLabelsToDestination(URLVars.nodePath+_pathSeparator+label, avoidDuplicates);
                    });
                } else {
                    pasteLabelsToDestination(toPath, avoidDuplicates);
                }
                // Update the state after all labels have been pasted
                get().updateVisibleLabels(newNodes);
                const { isSubscribed } = useUserStore.getState();
                set({
                    nodes: newNodes,
                    nodesCount: countNodes(newNodes),
                    freeNodes: getFirstFreeCards(newNodes, isSubscribed || false)
                });
            }, 

            updateVisibleLabels: (nodes:any) => {
                const { URLVars } = useURLStore.getState();
                const visibleLabels = nodes[URLVars.nodePath!];
                
                useURLStore.setState({ visibleLabels: visibleLabels });
            },

            setActivityDict: (activityDict:  { [key: string]: { [key:string] : any } }) => {    
                set({ activityDict: activityDict });
            },

            resetSnapshot: () => {
                set({ snapshot: null });
            },

            getImage: (label: string) => {
                const l2Image = get().l2Image;
                // If there is a direct match, return it
                if (l2Image[label]) return l2Image[label];
                // Otherwise, try to find the root label and return the image for that
                const labelRootName = getLabelRootName(label);
                if(l2Image[labelRootName]) return l2Image[labelRootName];
                // Otherwise, return any image having key starting with the root label
                //const keys = Object.keys(l2Image);
                //for (let i = 0; i < keys.length; i++) {
                //    if (keys[i].startsWith(labelRootName)) return l2Image[keys[i]];
                //}
                return "";
            }


        }),{ 
            name: 'treeState',
            storage: customStorage,
            /*partialize:(state) =>({
                    nodes: state.nodes,
                    l2Visibility: state.l2Visibility,
                    l2Audio: state.l2Audio,
                    l2Image: state.l2Image,
                    l2Color: state.l2Color,
                    cachedLocalAutoL2image: state.cachedLocalAutoL2image
                    
            }),*/
            onRehydrateStorage: () => async (state, error) => {
                if (!error && state) {
                    //await state.migrate();
                    state.setTreeStoreInitialized(true);                    
                }
            }
    })
    )
);

